import { useEffect, useState } from 'react';
import useAppStore from '../store';

// Watchdog fires at 4× the 5s heartbeat interval (createHeartbeat default).
export const HEARTBEAT_STALE_MS = 20000;
const TICK_MS = 1000; // UI tick: staleness detection + per-second "x ago" labels

/**
 * Heartbeat staleness watchdog (S5.3/S7) — pure derivation over the realtime
 * slice; the transport itself is mounted once in App.jsx so multiple consumers
 * of this hook never duplicate heartbeats.
 *
 * Effective status vocabulary (user-facing, derived):
 * - 'no-transport'  no WS/SSE connection exists in this build (cutover §3:
 *                   the simulated heartbeat was removed; the slice boots here)
 * - 'connected'     heartbeat fresh (≤ HEARTBEAT_STALE_MS old)
 * - 'stale'         store says connected but the heartbeat aged past the
 *                   watchdog threshold — backend silent, auto-reconnect may
 *                   still be grinding
 * - 'disconnected'  auto-reconnect exhausted (5 attempts — S5.4 policy)
 * - 'connecting'    transport re-establishing (manual reconnect in flight)
 *
 * The realtime slice boots at 'no-transport' and only a REAL transport
 * (B7) mounted in App.jsx may move it out — heartbeats arrive on an
 * interval, staleness is timeout-detected, reconnect re-subscribes by topic
 * (holds under either fan-out posture; see
 * docs/planned_work/connection-fanout-decision.md).
 */
function useRealtimeConnection() {
  const connectionStatus = useAppStore((state) => state.connectionStatus);
  const lastHeartbeat = useAppStore((state) => state.lastHeartbeat);
  const reconnectNonce = useAppStore((state) => state.reconnectNonce);
  const requestReconnect = useAppStore((state) => state.requestReconnect);
  const [now, setNow] = useState(() => Date.now());

  // Always-on 1s tick: staleness must be detectable even when no heartbeat
  // arrives to re-render us, and indicator labels refresh per-second.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  const heartbeatAgeMs = lastHeartbeat ? now - new Date(lastHeartbeat).getTime() : null;
  const isStale = connectionStatus === 'connected'
    && heartbeatAgeMs !== null
    && heartbeatAgeMs >= HEARTBEAT_STALE_MS;

  const effectiveStatus = connectionStatus === 'connected' && isStale
    ? 'stale'
    : connectionStatus;

  return {
    connectionStatus: effectiveStatus,
    transportStatus: connectionStatus,
    lastHeartbeat,
    heartbeatAgeMs,
    isStale,
    isDisconnected: effectiveStatus === 'disconnected',
    reconnectNonce,
    requestReconnect
  };
}

export default useRealtimeConnection;
